import { Injectable } from '@angular/core';

export interface CookieOptions {
  domain?: string;
  path?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
  crossSubdomain?: boolean;
}

@Injectable({ providedIn: 'root' })
export class CookieService {
  private readonly DEFAULT_PATH = '/';
  private readonly DEFAULT_SAME_SITE: 'Strict' | 'Lax' | 'None' = 'Lax';
  private readonly COOKIE_NAME_REGEX = /^[a-zA-Z0-9!#$%&'*+\-.^_`|~]+$/;

  setCookie(
    name: string,
    value: string,
    daysToExpire: number,
    options: CookieOptions = {}
  ): void {
    if (!this.isValidCookieName(name) || !value) {
      return;
    }

    const { crossSubdomain, ...cookieOptions } = options;

    if (crossSubdomain && cookieOptions.domain) {
      console.error(
        `Cannot specify both 'crossSubdomain: true' and 'domain: "${cookieOptions.domain}"'.`
      );
      return;
    }

    const targetDomain = crossSubdomain
      ? this.getParentDomain()
      : cookieOptions.domain;

    if (crossSubdomain && targetDomain) {
      // Clear cookies from both the current hostname (default domain) and the target domain
      this.clearCookie(name, {
        path: cookieOptions.path,
        sameSite: cookieOptions.sameSite,
        secure: cookieOptions.secure,
        domain: this.getHostname(),
      });

      this.clearCookie(name, {
        path: cookieOptions.path,
        sameSite: cookieOptions.sameSite,
        secure: cookieOptions.secure,
        domain: targetDomain,
      });
    }

    const expires = new Date();
    expires.setTime(expires.getTime() + daysToExpire * 24 * 60 * 60 * 1000);

    document.cookie = `${name}=${value}; ${this.buildCookieOptions({
      expires,
      path: cookieOptions.path,
      domain: targetDomain,
      sameSite: cookieOptions.sameSite,
      secure: cookieOptions.secure,
    })}`;
  }

  getCookie(name: string): string | null {
    if (!this.isValidCookieName(name)) {
      return null;
    }

    const cookies = document.cookie.split(';').map(c => c.trim());
    const existingCookie = cookies.find(c => c.startsWith(`${name}=`));
    return existingCookie ? existingCookie.split('=')[1] : null;
  }

  clearCookie(name: string, options: CookieOptions = {}): void {
    const { crossSubdomain, ...cookieOptions } = options;
    const parentDomain = crossSubdomain
      ? this.getParentDomain()
      : cookieOptions.domain;

    document.cookie = `${name}=; ${this.buildCookieOptions({
      expires: new Date(0),
      path: cookieOptions.path,
      domain: parentDomain,
      sameSite: cookieOptions.sameSite,
      secure: cookieOptions.secure,
    })}`;
  }

  private isValidCookieName(name: string): boolean {
    return typeof name === 'string' && this.COOKIE_NAME_REGEX.test(name);
  }

  private getHostname(): string {
    return window.location.hostname;
  }

  private getParentDomain(): string | undefined {
    const host = this.getHostname();
    if (host === 'localhost' || host === '127.0.0.1') {
      return undefined;
    }
    const parts = host.split('.');

    return parts.length >= 2 ? `.${parts.slice(-2).join('.')}` : undefined;
  }

  private buildCookieOptions(options: {
    expires?: Date;
    path?: string;
    domain?: string;
    sameSite?: string;
    secure?: boolean;
  }): string {
    const parts = [];
    if (options.expires) {
      parts.push(`expires=${options.expires.toUTCString()}`);
    }
    parts.push(`path=${options.path ?? this.DEFAULT_PATH}`);
    if (options.domain) {
      parts.push(`domain=${options.domain}`);
    }
    parts.push(`SameSite=${options.sameSite ?? this.DEFAULT_SAME_SITE}`);
    if (options.secure || options.sameSite === 'None') {
      parts.push('Secure');
    }

    return parts.join('; ');
  }
}
